import {Fraction} from './Fraction'
import { getBarModel, normalizeMeter, parseNoteLengthParts } from './barModel'
import abcjs from "abcjs";
import useUtils from './useUtils'
import { chordParserFactory, chordRendererFactory } from 'chord-symbol';
import { renderDeletedTunesToAbc } from './tuneBookSync'
import { stripPerformanceSetLines } from './performanceSetSync'
import { stripPlaylistLines } from './playlistSync'
import { stripPracticeListLines } from './practiceListSync'
import { buildLegacyTablatureConfigMap, parseTablatureVoices } from './tablatureConfig.js'
import { getInterleavedLyricLines, renderBlockLyricsAbc } from './wLinesUtils'
import {
  mergeBibliographicList,
  normalizeBibliographicFields,
  normalizeTuneGenres,
  pushInfoHeaderValue,
  renderAbcInfoHeaderLines,
  renderBibliographicComposerLines,
  renderBibliographicGenreLines,
  renderBibliographicTitleLines,
  renderSourceBookCommentLines,
  salvageBackgroundMetaSeeds,
  splitSlashJoinedTitle,
} from './tuneBibliographicUtils'
import { renderTimedJsonFields, applyAbcbookJsonChunks, collectAbcbookJsonChunk, parseAbcbookJsonLine, LEGACY_TIMED_JSON_FIELDS } from './abcbookJsonFields'
import { convertSessionLineBreaks } from './abcImportNormalize'
import { resolvePrimaryVoiceKey } from './abcVoiceUtils'
import { parsePlayalongTakeComment, renderPlayalongTakesAbc } from './playalongTakes'

var useAbcTools = () => {
    var utils = useUtils()
    var requiredHeaders = ['X','K','M','L','T','R','B']

    
    function isCommentLine(line) {
        var t = String(line || '').trim()
        return ((t.startsWith('% ') || t.startsWith('%%')) && !isDataLine(t) && !t.startsWith('%%MIDI '))
    }

    function isDataLine(line) {
        return String(line || '').trim().startsWith('% abcbook-')
    }

    function abcbookFieldValue(line, prefix) {
        return line.slice(prefix.length).trim()
    }

    function renderPlaybackAbcbookFields(tune) {
        var tempo = tune.playbackTempo > 0 ? parseFloat(tune.playbackTempo) : 1
        var pitch = tune.playbackPitch !== undefined && tune.playbackPitch !== null && tune.playbackPitch !== ''
            ? parseInt(tune.playbackPitch, 10) : 0
        var fineTune = tune.playbackFineTune !== undefined && tune.playbackFineTune !== null && tune.playbackFineTune !== ''
            ? parseInt(tune.playbackFineTune, 10) : 0
        var lines = "% abcbook-playback-tempo " + tempo + "\n"
            + "% abcbook-playback-transpose " + pitch + "\n"
            + "% abcbook-playback-fine-tune " + fineTune + "\n"
        if (tune.playbackMetronomeCountIn === false) {
            lines += "% abcbook-playback-metronome-count-in false\n"
        }
        if (tune.playbackMetronomeCountInBars > 0 && tune.playbackMetronomeCountInBars !== 1) {
            lines += "% abcbook-playback-metronome-count-in-bars " + tune.playbackMetronomeCountInBars + "\n"
        }
        if (tune.playbackMetronomeDuringPlayback === true) {
            lines += "% abcbook-playback-metronome-during-playback true\n"
        }
        if (tune.playbackMetronomeClickRhythm) {
            try {
                lines += "% abcbook-playback-metronome-click-rhythm " + JSON.stringify(tune.playbackMetronomeClickRhythm) + "\n"
            } catch (e) {}
        }
        if (tune.playbackMetronomeDrumRhythm) {
            try {
                lines += "% abcbook-playback-metronome-drum-rhythm " + JSON.stringify(tune.playbackMetronomeDrumRhythm) + "\n"
            } catch (e) {}
        }
        if (tune.playbackMetronomeRhythm) {
            try {
                lines += "% abcbook-playback-metronome-rhythm " + JSON.stringify(tune.playbackMetronomeRhythm) + "\n"
            } catch (e) {}
        }
        if (tune.playbackMetronomeEngine && tune.playbackMetronomeEngine !== 'click') {
            lines += "% abcbook-playback-metronome-engine " + tune.playbackMetronomeEngine + "\n"
        }
        if (tune.playbackMetronomePresetId) {
            lines += "% abcbook-playback-metronome-preset-id " + tune.playbackMetronomePresetId + "\n"
        }
        if (tune.playbackFillStyle && tune.playbackFillStyle !== 'boom-chick') {
            lines += "% abcbook-playback-fill-style " + tune.playbackFillStyle + "\n"
        }
        if (tune.playbackFillLevel != null && tune.playbackFillLevel !== 100) {
            lines += "% abcbook-playback-fill-level " + tune.playbackFillLevel + "\n"
        }
        return lines
    }
    
    function isVoiceMeta(line) {
        return String(line || '').trim().startsWith('V:')
    }
    
    function isNoteLine(line) {
        return (!isCommentLine(line) && !isDataLine(line) && !isMetaLine(line))
    }
    
    function isMetaLine(line) {
        var t = String(line || '').trim()
        return (t.length > 1 && t[1] === ":" && t[0] !=='|')
    }
    
    function justNotesHasCollectedBody(lines) {
        // Leading blank lines before the first V: must not count — otherwise the
        // first voice header aborts extraction and chord saves wipe the tune.
        for (var i = 0; i < lines.length; i++) {
            if (String(lines[i] || '').trim() !== '') return true
        }
        return false
    }

    function justNotes(text) {
        var final = []
        var parts = String(text || '').split("\n")
        for (var lineNumber in parts) {
            var line = parts[lineNumber]
            var trimmed = String(line || '').trim()
            // Drop blanks: ABC end-of-tune blanks truncate chords in abcjs.
            if (!trimmed) continue
            if (isNoteLine(trimmed)) {
                final.push(trimmed)
            }
            if (isVoiceMeta(trimmed) && justNotesHasCollectedBody(final)) {
                break
            }
        }
        return final.join("\n")
        //return text.split("\n").filter(function(line) {
            //return line.trim() === "" || isNoteLine(line)
        //}).join("\n")
    }
    
    function justNotesNoMeta(text) {
        var final = []
        var parts = String(text || '').split("\n")
        for (var lineNumber in parts) {
            var line = parts[lineNumber]
            var trimmed = String(line || '').trim()
            if (!trimmed) continue
            if (isNoteLine(trimmed) && !isDataLine(trimmed) && !trimmed.startsWith('%%MIDI ')) {
                final.push(trimmed)
            }
            if (isVoiceMeta(trimmed) && justNotesHasCollectedBody(final)) {
                break
            }
        }
        return final.join("\n")
        //return text.split("\n").filter(function(line) {
            //return line.trim() === "" || isNoteLine(line)
        //}).join("\n")
    }
    
   
    /** Plain object only — rejects null, arrays, and String wrappers / "[object Object]". */
    function isPlainTuneMeta(meta) {
        return !!(
            meta
            && typeof meta === 'object'
            && !Array.isArray(meta)
            && Object.prototype.toString.call(meta) === '[object Object]'
        )
    }

    function normalizeTuneMeta(meta) {
        return isPlainTuneMeta(meta) ? Object.assign({}, meta) : {}
    }

    function pushMeta(meta, key, line) {
        if (!isPlainTuneMeta(meta)) meta = {}
        if (!(Object.prototype.hasOwnProperty.call(meta, key) && Array.isArray(meta[key]))) {
            meta[key] = []
        }
        var text = String(line == null ? '' : line).trim()
        if (text) meta[key].push(text)
        return meta
    }

    var ABC_LETTER_TO_INFO_FIELD = {
      O: 'origin',
      A: 'area',
      S: 'source',
      Z: 'transcription',
      D: 'discography',
      N: 'infoNotes',
    }

    // Typed info headers are emitted separately; never re-emit from meta catch-all.
    var TYPED_INFO_META_KEYS = {
      O: true,
      A: true,
      S: true,
      Z: true,
      D: true,
      N: true,
    }

    // Primary headers json2abc already writes before otherHeaders. Re-emitting
    // X: mid-header makes abcjs treat the rest as a second tune and drop notes.
    var PRIMARY_EMITTED_META_KEYS = {
      X: true,
      T: true,
      M: true,
      L: true,
      R: true,
      Q: true,
      K: true,
      C: true,
      B: true,
      H: true,
      G: true,
    }

    function normalizeGenre(tune) {
        return normalizeTuneGenres(tune)
    }

    // App-internal meta (objects / non-ABC). Never emit as header lines.
    // chordProSource: original ChordPro / lyric-chord sheet (lyric placement truth).
    // chordSheetAlignment: word↔beat anchors for ABC scaffold merge (optional bridge).
    var INTERNAL_META_KEYS = {
      chordSheetAlignment: true,
      chordProSource: true,
    }

    function isAbcHeaderMetaValue(value) {
      return typeof value === 'string' || typeof value === 'number'
    }

    function renderOtherHeaders(tune) {
      if (tune && tune.meta) {
        return Object.keys(tune.meta).map(function(key) {
          if (PRIMARY_EMITTED_META_KEYS[key] || INTERNAL_META_KEYS[key] || TYPED_INFO_META_KEYS[key]) {
            return ''
          }
          var values = tune.meta[key]
          if (Array.isArray(values)) {
            return values.map(function(metaLine) {
              if (!isAbcHeaderMetaValue(metaLine)) return ''
              return key + ": " + metaLine + "\n"
            }).join("")
          }
          if (isAbcHeaderMetaValue(values)) {
            return key + ": " + values + "\n"
          }
          return ""
        }).join("")
      } else {
         return "" 
      }
    }

    
    function getBeatLength(meter) {
          switch (meter) {
            case '2/2':
              return '1/2'
            case '3/2':
              return '1/2'
            case '4/2':
              return '1/2'
            case '3/8':
              return '3/8'
            case '6/8':
              return '3/8'
            case '9/8':
              return '3/8'
            case '12/8':
              return '3/8'
            case '2/4':
              return '1/4'
            case '3/4':
              return '1/4'
            case '4/4':
              return '1/4'
            case '6/4':
              return '3/4'
            case '9/4':
              return '3/4'
          }
          return '1/4'
        
    }

    
    function abc2json(abc, options) {
      var opts = options && typeof options === 'object' ? options : {}
      if (abc && abc.trim().length > 0) {
        var tune = {id: null, name: null,books:[], sourceBooks: [], voices:{'1':{meta:'',notes:[]}}, tempo: 100, rhythm:null, genres: [], albums: [], noteLength: null, meter: null,key:null, boost: 0, starred: false, aliases:[], artists:[], origin: [], area: [], source: [], transcription: [], discography: [], infoNotes: [], abccomments:[], capo: 0, playbackTempo: 1, playbackPitch: 0, playbackFineTune: 0, notes:[], words: [], wLines: [], timingScaffold: false, backgroundInfo: '', lyricsScrollDurationSec: 0, meta: {}}
        var currentVoice = '1'
        var pendingMidiPrograms = []
        var seenVoiceHeader = false
        var seenBodyVoiceSelector = false
        var declaredVoiceKeys = {}
        var links = {}
         var files = {}
         var tuneFiles = {}
         var recordings = {}
         var abcbookJsonChunks = {}
         var hLines = []
         var pendingBooks = []
         var hadAbcbookTuneId = false
         abc.split("\n").forEach(function(line) {
            line = String(line || '').trim()
            if (!line) return
            var capoMatch = line.match(/^capo:\s*(\d+)/i) || line.match(/^%%capo\s+(\d+)/i)
            if (capoMatch) {
                tune.capo = parseInt(capoMatch[1], 10) || 0
                return
            }
            if (isCommentLine(line)) {
                tune.abccomments.push(line)
            } else if (isMetaLine(line)) {
                var key = line[0]
                switch (key) {
                    case "W":
                        tune.words.push(line.slice(2).trim())
                        break
                    case "w":
                        if (!Array.isArray(tune.wLines)) tune.wLines = []
                        tune.wLines.push(line.slice(2).trim())
                        break
                    case "V":
                        {
                            var voiceParts = line.slice(2).trim().split(/\s+/).filter(Boolean)
                            if (voiceParts[0]) {
                                var voiceKey = voiceParts[0]
                                var voiceMeta = voiceParts.slice(1).join(' ')
                                // First V:N declares the voice; later bare V:N lines are body
                                // selectors (xml2abc / multi-staff style) and must not wipe
                                // clef/nm meta or notes already collected for that voice.
                                var voiceAlreadyDeclared = Object.prototype.hasOwnProperty.call(
                                    declaredVoiceKeys,
                                    voiceKey
                                )
                                if (!voiceAlreadyDeclared) {
                                    declaredVoiceKeys[voiceKey] = true
                                    tune.voices[voiceKey] = {meta: voiceMeta, notes: []}
                                    if (pendingMidiPrograms.length > 0) {
                                        tune.voices[voiceKey].notes.unshift(
                                            '%%MIDI program ' + pendingMidiPrograms.shift()
                                        )
                                    }
                                } else {
                                    if (voiceMeta) {
                                        tune.voices[voiceKey].meta = voiceMeta
                                    }
                                    if (!Array.isArray(tune.voices[voiceKey].notes)) {
                                        tune.voices[voiceKey].notes = []
                                    }
                                }
                                seenVoiceHeader = true
                                currentVoice = voiceKey
                            }
                        }
                        break
                    case "T":
                        {
                            var titleText = line.slice(2).trim()
                            if (!tune.name) {
                                var slashSplit = splitSlashJoinedTitle(titleText)
                                if (slashSplit && slashSplit.split) {
                                    tune.name = slashSplit.name
                                    tune.aliases = mergeBibliographicList(tune.aliases, slashSplit.aliases)
                                } else {
                                    tune.name = titleText
                                }
                            } else {
                                tune.aliases = mergeBibliographicList(tune.aliases, titleText)
                            }
                        }
                        break
                    case "B":
                        pendingBooks.push(line.slice(2).trim())
                        break
                    case "M":
                        // only the first one
                        if (!tune.meter) {
                            tune.meter = line.slice(2).trim()
                        }
                        break
                    case "R":
                        if (!tune.rhythm) {
                            tune.rhythm = line.slice(2).trim()
                        }
                        break
                    case "L":
                        if (!tune.noteLength) {
                            tune.noteLength = line.slice(2).trim()
                        }
                        break
                    case "C":
                        {
                            var composerText = line.slice(2).trim()
                            if (!tune.composer) {
                                tune.composer = composerText
                            } else {
                                tune.artists = mergeBibliographicList(tune.artists, composerText)
                            }
                        }
                        break
                    case "G":
                        {
                            if (!Array.isArray(tune.genres)) tune.genres = []
                            tune.genres = mergeBibliographicList(tune.genres, line.slice(2).trim())
                        }
                        break
                    case "K":
                        if (!tune.key) tune.key = line.slice(2).trim()
                        break
                    case "H":
                        hLines.push(line.slice(2))
                        break
                    case "Q":
                        tune.tempo = cleanTempo(line.slice(2).trim())
                        break
                    case "N":
                        {
                            var noteBody = line.slice(2).trim()
                            if (/^AKA:\s*/i.test(noteBody)) {
                                if (!Array.isArray(tune.aliases)) tune.aliases = []
                                noteBody.replace(/^AKA:\s*/i, '').split(',').forEach(function(aliasPart) {
                                    var alias = aliasPart.trim()
                                    if (alias && tune.aliases.indexOf(alias) === -1) {
                                        tune.aliases.push(alias)
                                    }
                                })
                            } else {
                                pushInfoHeaderValue(tune, 'infoNotes', noteBody)
                            }
                        }
                        break
                    case "O":
                    case "A":
                    case "S":
                    case "Z":
                    case "D":
                        pushInfoHeaderValue(tune, ABC_LETTER_TO_INFO_FIELD[key], line.slice(2).trim())
                        break
                    default:
                        tune.meta = pushMeta(tune.meta, key, line.slice(2).trim())
                        break
                }
            } else if (isDataLine(line)) {
                if (line.startsWith('% abcbook-tune_id')) {
                    hadAbcbookTuneId = true
                    tune.id = line.slice(17).trim()
                } else if (line.startsWith('% abcbook-source-book')) {
                    pushInfoHeaderValue(tune, 'sourceBooks', abcbookFieldValue(line, '% abcbook-source-book'))
                } else if (line.startsWith('% abcbook-tune_composer_id')) {
                    tune.composerId = line.slice(26).trim()
                } else if (line.startsWith('% abcbook-boost')) {
                    tune.boost = parseInt(line.slice(16).trim())
                } else if (line.startsWith('% abcbook-starred')) {
                    var starredVal = abcbookFieldValue(line, '% abcbook-starred')
                    tune.starred = starredVal === 'true' || starredVal === '1'
                } else if (line.startsWith('% abcbook-auto-enrich')) {
                    var autoEnrichVal = abcbookFieldValue(line, '% abcbook-auto-enrich')
                    tune.autoEnrichOnOpen = autoEnrichVal === 'pending' || autoEnrichVal === 'true'
                } else if (line.startsWith('% abcbook-difficulty')) {
                    tune.difficulty = parseInt(line.slice(21).trim())
                } else  if (line.startsWith('% abcbook-tablature-voices')) {
                    try {
                      tune.tablatureVoices = parseTablatureVoices(
                        JSON.parse(abcbookFieldValue(line, '% abcbook-tablature-voices'))
                      )
                    } catch (e) {
                      tune.tablatureVoices = null
                    }
                } else  if (line.startsWith('% abcbook-tablature-enabled')) {
                    var tabEnabledVal = abcbookFieldValue(line, '% abcbook-tablature-enabled')
                    tune.tablatureEnabled = tabEnabledVal !== 'false' && tabEnabledVal !== '0'
                } else  if (line.startsWith('% abcbook-tablature')) {
                    tune.tablature = line.slice(20).trim()
                } else  if (line.startsWith('% abcbook-tab-display')) {
                    var tabDisplayVal = abcbookFieldValue(line, '% abcbook-tab-display')
                    tune.tabDisplay = tabDisplayVal === 'tab' ? 'tab' : 'both'
                } else  if (line.startsWith('% abcbook-capo')) {
                    tune.capo = parseInt(abcbookFieldValue(line, '% abcbook-capo'), 10) || 0
                } else  if (line.startsWith('% abcbook-playback-tempo')) {
                    var playbackTempoVal = parseFloat(abcbookFieldValue(line, '% abcbook-playback-tempo'))
                    tune.playbackTempo = playbackTempoVal > 0 ? playbackTempoVal : 1
                } else  if (line.startsWith('% abcbook-playback-transpose')) {
                    tune.playbackPitch = parseInt(abcbookFieldValue(line, '% abcbook-playback-transpose'), 10) || 0
                } else  if (line.startsWith('% abcbook-playback-fine-tune')) {
                    tune.playbackFineTune = parseInt(abcbookFieldValue(line, '% abcbook-playback-fine-tune'), 10) || 0
                } else  if (line.startsWith('% abcbook-playback-metronome-count-in')) {
                    var countInVal = abcbookFieldValue(line, '% abcbook-playback-metronome-count-in')
                    tune.playbackMetronomeCountIn = countInVal !== 'false' && countInVal !== '0'
                } else  if (line.startsWith('% abcbook-playback-metronome-count-in-bars')) {
                    var countInBarsVal = parseInt(abcbookFieldValue(line, '% abcbook-playback-metronome-count-in-bars'), 10)
                    tune.playbackMetronomeCountInBars = countInBarsVal > 0 ? countInBarsVal : 1
                } else  if (line.startsWith('% abcbook-playback-metronome-during-playback')) {
                    var duringPlaybackVal = abcbookFieldValue(line, '% abcbook-playback-metronome-during-playback')
                    tune.playbackMetronomeDuringPlayback = duringPlaybackVal === 'true' || duringPlaybackVal === '1'
                } else  if (line.startsWith('% abcbook-playback-metronome-click-rhythm')) {
                    try {
                        tune.playbackMetronomeClickRhythm = JSON.parse(abcbookFieldValue(line, '% abcbook-playback-metronome-click-rhythm'))
                    } catch (e) {}
                } else  if (line.startsWith('% abcbook-playback-metronome-drum-rhythm')) {
                    try {
                        tune.playbackMetronomeDrumRhythm = JSON.parse(abcbookFieldValue(line, '% abcbook-playback-metronome-drum-rhythm'))
                    } catch (e) {}
                } else  if (line.startsWith('% abcbook-playback-metronome-rhythm')) {
                    try {
                        tune.playbackMetronomeRhythm = JSON.parse(abcbookFieldValue(line, '% abcbook-playback-metronome-rhythm'))
                    } catch (e) {}
                } else  if (line.startsWith('% abcbook-playback-metronome-engine')) {
                    tune.playbackMetronomeEngine = abcbookFieldValue(line, '% abcbook-playback-metronome-engine')
                } else  if (line.startsWith('% abcbook-playback-metronome-preset-id')) {
                    tune.playbackMetronomePresetId = abcbookFieldValue(line, '% abcbook-playback-metronome-preset-id')
                } else  if (line.startsWith('% abcbook-playback-fill-style')) {
                    tune.playbackFillStyle = abcbookFieldValue(line, '% abcbook-playback-fill-style')
                } else  if (line.startsWith('% abcbook-playback-fill-level')) {
                    var fillLevelVal = parseInt(abcbookFieldValue(line, '% abcbook-playback-fill-level'), 10)
                    tune.playbackFillLevel = fillLevelVal >= 0 ? fillLevelVal : 100
                } else  if (line.startsWith('% abcbook-lyrics-scroll-speed')) {
                    var lyricsScrollSpeedVal = parseFloat(abcbookFieldValue(line, '% abcbook-lyrics-scroll-speed'))
                    tune.lyricsScrollSpeed = lyricsScrollSpeedVal > 0 ? lyricsScrollSpeedVal : 1
                } else  if (line.startsWith('% abcbook-lyrics-scroll-duration')) {
                    var lyricsScrollDurationVal = parseInt(abcbookFieldValue(line, '% abcbook-lyrics-scroll-duration'), 10)
                    tune.lyricsScrollDurationSec = lyricsScrollDurationVal > 0 ? lyricsScrollDurationVal : 0
                } else  if (line.startsWith('% abcbook-zoom')) {
                    var zoomVal = parseFloat(abcbookFieldValue(line, '% abcbook-zoom'))
                    tune.zoom = zoomVal > 0 ? zoomVal : undefined
                } else  if (line.startsWith('% abcbook-view-mode')) {
                    var viewModeVal = abcbookFieldValue(line, '% abcbook-view-mode')
                    if (viewModeVal) tune.viewMode = viewModeVal
                } else  if (line.startsWith('% abcbook-notation-fit')) {
                    var fitVal = abcbookFieldValue(line, '% abcbook-notation-fit')
                    if (fitVal === 'vertical' || fitVal === 'horizontal') tune.notationFit = fitVal
                } else  if (line.startsWith('% abcbook-active-voices')) {
                    var activeVoicesVal = abcbookFieldValue(line, '% abcbook-active-voices')
                    tune.activeVoices = activeVoicesVal
                      ? activeVoicesVal.split(/[,\s]+/).map(function(part) { return part.trim() }).filter(Boolean)
                      : []
                } else  if (line.startsWith('% abcbook-transpose')) {
                    tune.transpose = line.slice(20).trim()
                } else  if (line.startsWith('% abcbook-tuning')) {
                    tune.tuning = line.slice(17).trim()
                } else  if (line.startsWith('% abcbook-tags')) {
                    tune.tags = line.slice(14).trim() ? line.slice(14).trim().split(",") : []
                } else  if (line.startsWith('% abcbook-albums ')) {
                    if (!Array.isArray(tune.albums)) tune.albums = []
                    const albumVal = line.slice(16).trim()
                    if (albumVal) tune.albums.push(albumVal)
                } else  if (line.startsWith('% abcbook-lastupdated')) {
                    tune.lastUpdated = line.slice(22).trim()
                } else  if (line.startsWith('% abcbook-soundfonts')) {
                    tune.soundFonts = line.slice(21).trim()
                } else  if (line.startsWith('% abcbook-repeats')) {
                    tune.repeats = line.slice(17).trim()
                } else if (line.startsWith('% abcbook-json ')) {
                    abcbookJsonChunks = collectAbcbookJsonChunk(parseAbcbookJsonLine(line), abcbookJsonChunks)
               } else if (line.startsWith('% abcbook-timing-scaffold')) {
                    tune.timingScaffold = abcbookFieldValue(line, '% abcbook-timing-scaffold') === 'true'
               } else  if (line.startsWith('% abcbook-src-url')) {
                    tune.srcUrl = line.slice(17).trim()
               } else if (line.startsWith('% abcbook-active-file')) {
                    tune.activeFile = abcbookFieldValue(line, '% abcbook-active-file')
               } else if (line.startsWith('% abcbook-file-id-')) {
                    var parts = line.trim().split('% abcbook-file-id-')
                    if (parts.length > 1) {
                        var numberParts = parts[1].split(' ')
                        if (numberParts.length > 1) {
                            if (!tuneFiles[numberParts[0]]) tuneFiles[numberParts[0]] = {}
                            tuneFiles[numberParts[0]].id = numberParts.slice(1).join(' ')
                        }
                    }
               } else if (line.startsWith('% abcbook-file-google-id-')) {
                    var parts = line.trim().split('% abcbook-file-google-id-')
                    if (parts.length > 1) {
                        var numberParts = parts[1].split(' ')
                        if (numberParts.length > 1) {
                            if (!tuneFiles[numberParts[0]]) tuneFiles[numberParts[0]] = {}
                            tuneFiles[numberParts[0]].googleId = numberParts.slice(1).join(' ')
                        }
                    }
               } else if (line.startsWith('% abcbook-file-source-')) {
                    var parts = line.trim().split('% abcbook-file-source-')
                    if (parts.length > 1) {
                        var numberParts = parts[1].split(' ')
                        if (numberParts.length > 1) {
                            if (!tuneFiles[numberParts[0]]) tuneFiles[numberParts[0]] = {}
                            tuneFiles[numberParts[0]].source = numberParts.slice(1).join(' ')
                        }
                    }
               } else if (line.startsWith('% abcbook-file-pdf-page-')) {
                    var parts = line.trim().split('% abcbook-file-pdf-page-')
                    if (parts.length > 1) {
                        var numberParts = parts[1].split(' ')
                        if (numberParts.length > 1) {
                            if (!tuneFiles[numberParts[0]]) tuneFiles[numberParts[0]] = {}
                            tuneFiles[numberParts[0]].pdfPage = parseInt(numberParts.slice(1).join(' '), 10) || 1
                        }
                    }
               } else if (line.startsWith('% abcbook-file-pdf-segments-')) {
                    var parts = line.trim().split('% abcbook-file-pdf-segments-')
                    if (parts.length > 1) {
                        var numberParts = parts[1].split(' ')
                        if (numberParts.length > 1) {
                            if (!tuneFiles[numberParts[0]]) tuneFiles[numberParts[0]] = {}
                            try {
                                tuneFiles[numberParts[0]].pdfSegments = JSON.parse(numberParts.slice(1).join(' '))
                            } catch (e) {
                                tuneFiles[numberParts[0]].pdfSegments = []
                            }
                        }
                    }
               } else  if (line.startsWith('% abcbook-file-')) {
                    if (line.startsWith('% abcbook-file-type-')) {
                        var parts = line.trim().split('% abcbook-file-type-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (tuneFiles[numberParts[0]]) {
                                    tuneFiles[numberParts[0]].type = numberParts.slice(1).join(' ')
                                } else {
                                    if (!files[numberParts[0]]) files[numberParts[0]] = {}
                                    files[numberParts[0]].type = numberParts.slice(1).join(' ')
                                }
                            }
                        }
                    } else if (line.startsWith('% abcbook-file-google-document-id-')) {
                        var parts = line.trim().split('% abcbook-file-google-document-id-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!files[numberParts[0]]) files[numberParts[0]] = {}
                                files[numberParts[0]].googleDocumentId = numberParts[1]
                            }
                        }
                    } else if (line.startsWith('% abcbook-file-name-')) {
                        var parts = line.trim().split('% abcbook-file-name-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (tuneFiles[numberParts[0]]) {
                                    tuneFiles[numberParts[0]].name = numberParts.slice(1).join(' ')
                                } else {
                                    if (!files[numberParts[0]]) files[numberParts[0]] = {}
                                    files[numberParts[0]].name = numberParts.slice(1).join(' ')
                                }
                            }
                        }
                    } else if (line.startsWith('% abcbook-file-data-')) {
                        var parts = line.trim().split('% abcbook-file-data-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                var fileDataPayload = numberParts.slice(1).join(' ')
                                if (tuneFiles[numberParts[0]] && tuneFiles[numberParts[0]].id) {
                                    tuneFiles[numberParts[0]].data = fileDataPayload
                                } else {
                                    if (!files[numberParts[0]]) files[numberParts[0]] = {}
                                    files[numberParts[0]].data = fileDataPayload
                                }
                            }
                        }
                    }
                } else  if (line.startsWith('% abcbook-recording-')) {
                    if (line.startsWith('% abcbook-recording-type-')) {
                        var parts = line.trim().split('% abcbook-recording-type-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!recordings[numberParts[0]]) recordings[numberParts[0]] = {}
                                recordings[numberParts[0]].type = numberParts.slice(1).join(' ')
                            }
                        }
                    } else if (line.startsWith('% abcbook-recording-google-document-id-')) {
                        var parts = line.trim().split('% abcbook-recording-google-document-id-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!recordings[numberParts[0]]) recordings[numberParts[0]] = {}
                                recordings[numberParts[0]].googleDocumentId = numberParts[1]
                            }
                        }
                    } else if (line.startsWith('% abcbook-recording-name-')) {
                        var parts = line.trim().split('% abcbook-recording-name-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!recordings[numberParts[0]]) recordings[numberParts[0]] = {}
                                recordings[numberParts[0]].name = numberParts.slice(1).join(' ')
                            }
                        }
                    } else if (line.startsWith('% abcbook-recording-data-')) {
                        var parts = line.trim().split('% abcbook-recording-data-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!recordings[numberParts[0]]) recordings[numberParts[0]] = {}
                                recordings[numberParts[0]].data = numberParts.slice(1).join(' ')
                            }
                        }
                    }
                } else if (line.startsWith('% abcbook-playalong-take-')) {
                    var parsedTake = parsePlayalongTakeComment(line)
                    if (parsedTake) {
                        if (!Array.isArray(tune.playalongTakes)) tune.playalongTakes = []
                        tune.playalongTakes.push(parsedTake)
                    }
                } else  if (line.startsWith('% abcbook-link-')) {
                    if (line.startsWith('% abcbook-link-title-')) {
                        var parts = line.trim().split('% abcbook-link-title-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!links[numberParts[0]]) links[numberParts[0]] = {}
                                links[numberParts[0]].title = numberParts.slice(1).join(' ')
                            }
                        }
                    } else if (line.startsWith('% abcbook-link-start-at-')) {
                        var parts = line.trim().split('% abcbook-link-start-at-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!links[numberParts[0]]) links[numberParts[0]] = {}
                                links[numberParts[0]].startAt = numberParts.slice(1).join(' ')
                            }
                        }
                    } else if (line.startsWith('% abcbook-link-end-at-')) {
                        var parts = line.trim().split('% abcbook-link-end-at-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!links[numberParts[0]]) links[numberParts[0]] = {}
                                links[numberParts[0]].endAt = numberParts.slice(1).join(' ')
                            }
                        }
                    } else if (line.startsWith('% abcbook-link-recording-id-')) {
                        var parts = line.trim().split('% abcbook-link-recording-id-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!links[numberParts[0]]) links[numberParts[0]] = {}
                                links[numberParts[0]].recordingId = numberParts.slice(1).join(' ')
                            }
                        }
                    } else if (line.startsWith('% abcbook-link-google-id-')) {
                        var parts = line.trim().split('% abcbook-link-google-id-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!links[numberParts[0]]) links[numberParts[0]] = {}
                                links[numberParts[0]].googleId = numberParts.slice(1).join(' ')
                            }
                        }
                    } else if (line.startsWith('% abcbook-link-media-kind-')) {
                        var parts = line.trim().split('% abcbook-link-media-kind-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!links[numberParts[0]]) links[numberParts[0]] = {}
                                links[numberParts[0]].mediaKind = numberParts.slice(1).join(' ')
                            }
                        }
                    } else {
                        var parts = line.trim().split('% abcbook-link-')
                        if (parts.length > 1) {
                            var numberParts = parts[1].split(' ')
                            if (numberParts.length > 1) {
                                if (!links[numberParts[0]]) links[numberParts[0]] = {}
                                links[numberParts[0]].link = numberParts.slice(1).join(' ')
                            }
                        }
                    }
                }
          
            } else if (isNoteLine(line)) {
                if (line.trim().length > 0) {
                    // Legacy leak: chordSheetAlignment objects were stringified into ABC.
                    if (/^chordSheetAlignment:\s*\[object Object\]/i.test(line.trim())) {
                        return
                    }
                    if (line.startsWith('%%MIDI transpose')) {
                        tune.transpose = line.slice(16).trim()
                    } else if (/^%%MIDI\s+program\s+\d+/i.test(line.trim())) {
                        var programMatch = line.trim().match(/^%%MIDI\s+program\s+(\d+)/i)
                        if (programMatch) {
                            if (!seenVoiceHeader) {
                                pendingMidiPrograms.push(parseInt(programMatch[1], 10))
                            } else {
                                if (!tune.voices.hasOwnProperty(currentVoice)) tune.voices[currentVoice] = {meta:'',notes:[]}
                                tune.voices[currentVoice].notes.push(line.trim())
                            }
                        }
                    } else if (line.trim().startsWith('[V:') && line.indexOf(']') !== -1 ) {
                        var selectorMatch = line.trim().match(/^\[V:(\d+)\](.*)$/)
                        if (selectorMatch) {
                            var voiceKey = selectorMatch[1]
                            var voiceNotes = selectorMatch[2].trim()
                            seenBodyVoiceSelector = true
                            if (!tune.voices.hasOwnProperty(voiceKey)) tune.voices[voiceKey] = {meta:'',notes:[]}
                            currentVoice = voiceKey
                            if (voiceNotes) {
                                tune.voices[voiceKey].notes.push(voiceNotes)
                            }
                        }
                    } else {
                        //tune.notes.push(line.trim())
                        if (!tune.voices.hasOwnProperty(currentVoice) ) tune.voices[currentVoice] = {meta:'',notes:[]}
                        tune.voices[currentVoice].notes.push(line)
                    }
                }
            } 
        })
        tune.links = Object.values(links)
        tune.files = Object.values(files)
        tune.tuneFiles = Object.keys(tuneFiles).sort(function(a, b) {
          return parseInt(a, 10) - parseInt(b, 10)
        }).map(function(key) {
          return tuneFiles[key]
        }).filter(function(meta) {
          return meta && meta.id
        })
        if (!tune.activeFile) tune.activeFile = ''
        tune.recordings = Object.values(recordings)
        var timedJsonFields = applyAbcbookJsonChunks(abcbookJsonChunks)
        Object.keys(timedJsonFields).forEach(function(fieldName) {
          if (LEGACY_TIMED_JSON_FIELDS.indexOf(fieldName) >= 0) return
          tune[fieldName] = timedJsonFields[fieldName]
        })
        if (hLines.length > 0 && !tune.backgroundInfo) {
          tune.backgroundInfo = hLines.join('\n')
        }
        // App-owned ABC keeps B: as tunebook membership. External imports with
        // bAsSourceBook (forceBook path) treat B: as bibliographic source books.
        if (opts.bAsSourceBook && !hadAbcbookTuneId) {
          pendingBooks.forEach(function(book) {
            pushInfoHeaderValue(tune, 'sourceBooks', book)
          })
          tune.books = []
        } else {
          pendingBooks.forEach(function(book) {
            var bookName = String(book || '').trim().toLowerCase()
            if (bookName) tune.books.push(bookName)
          })
        }
        salvageBackgroundMetaSeeds(tune)
        normalizeBibliographicFields(tune)
        LEGACY_TIMED_JSON_FIELDS.forEach(function(fieldName) {
          delete tune[fieldName]
        })
        if (tune.id === null)  tune.id = utils.generateObjectId()
          return normalizeGenre(tune)
      }
      return {}
    }
    
    /**
     * TO ABC
     */
    
    /**
     * Append metadata for song title,.. to abc string
     * for rendering complete tunes
     * @return multiline abc string starting with \nX:<tuneid>\n suitable for tune book 
     */
    function json2abc(tune) {
      //var bookData = {}
      if (tune) {
        normalizeGenre(tune)
        var titleHeaderText = renderBibliographicTitleLines(tune).join("\n")
        if (titleHeaderText) titleHeaderText += "\n"
        var composerHeaderText = renderBibliographicComposerLines(tune).join("\n")
        if (composerHeaderText) composerHeaderText += "\n"
        var genreHeaderText = renderBibliographicGenreLines(tune).join("\n")
        if (genreHeaderText) genreHeaderText += "\n"
        var boost = tune.boost > 0 ? tune.boost : 0
        var tuneNumber = tune && tune.meta && parseInt(tune.meta.X) !== NaN && tune.meta.X >= 0 ? tune.meta.X : parseInt(Math.random()*100000)
        var books = Array.isArray(tune.books) && tune.books.length > 0 ? tune.books.map(function(book) {
          //bookData[ensureText(book).toLowerCase()] = {}
          
          return  'B: '+ensureText(book).toLowerCase()
        }).join("\n") + "\n": ''
        function ensure(test,text) {
            if (test) {
                return text
            } else {
                return ''
            }
        }
        var tempoLine = ''
        if (cleanTempo(tune.tempo) > 0) {
             tempoLine = "Q: "+ getBeatLength(tune.meter)+'='+ (cleanTempo(tune.tempo) > 0 ? cleanTempo(tune.tempo) : '100') + "\n" 
        }
        var voicesAndNotes=[]
        if (tune.voices && Object.keys(tune.voices).length > 0) {
            var wLineIndex = 0
            var wLines = getInterleavedLyricLines(tune)
            var primaryVoiceKey = resolvePrimaryVoiceKey(tune.voices)
            Object.keys(tune.voices).forEach(function(voice) {
                voicesAndNotes.push("V:"+voice+" "+ensureText(tune.voices[voice].meta,""))
                var noteLines = Array.isArray(tune.voices[voice].notes)
                  ? tune.voices[voice].notes
                  : (tune.voices[voice].notes ? [String(tune.voices[voice].notes)] : [''])
                if (noteLines.length === 0) noteLines = ['']
                noteLines.forEach(function(noteLine) {
                        voicesAndNotes.push(noteLine)
                        if (voice === primaryVoiceKey && wLineIndex < wLines.length) {
                            var wText = wLines[wLineIndex]
                            if (wText !== null && wText !== undefined && String(wText).length > 0) {
                                voicesAndNotes.push('w: ' + wText)
                            }
                            wLineIndex += 1
                        }
                    })
            })
        } 
        var linksRendered = []
        if (Array.isArray(tune.links)&& tune.links.length > 0) {
            tune.links.forEach(function(link,k) {
                if (link.link) {
                    
                    linksRendered.push("% abcbook-link-"+k + ' ' +  ensureText(link.link,"") )
                    if (link.title) {
                        linksRendered.push("% abcbook-link-title-"+k + ' ' +  ensureText(link.title,"") )
                    }
                    if (link.startAt) {
                        linksRendered.push("% abcbook-link-start-at-"+k + ' ' +  ensureNumber(link.startAt,"") )
                    }
                    if (link.endAt) {
                        linksRendered.push("% abcbook-link-end-at-"+k + ' ' +  ensureNumber(link.endAt,"") )
                    }
                    if (link.recordingId) {
                        linksRendered.push("% abcbook-link-recording-id-"+k + ' ' +  ensureText(link.recordingId,"") )
                    }
                    if (link.googleId) {
                        linksRendered.push("% abcbook-link-google-id-"+k + ' ' +  ensureText(link.googleId,"") )
                    }
                    if (link.mediaKind) {
                        linksRendered.push("% abcbook-link-media-kind-"+k + ' ' +  ensureText(link.mediaKind,"") )
                    }
                }
                
            })
        }
        var filesRendered = []
        if (Array.isArray(tune.files)&& tune.files.length > 0) {
            tune.files.forEach(function(file,k) {
                if (file.googleDocumentId) {
                    filesRendered.push("% abcbook-file-google-document-id-"+k + ' ' +  ensureText(file.googleDocumentId,"") )
                }
                if (file.type) {
                        filesRendered.push("% abcbook-file-type-"+k + ' ' +  ensureText(file.type,"") )
				}
				if (file.name) {
					filesRendered.push("% abcbook-file-name-"+k + ' ' +  ensureText(file.name,"") )
				}
				if (file.data) {  
					filesRendered.push("% abcbook-file-data-"+k + ' ' +  ensureText(file.data,"") )
				}
            })
        }
        var tuneFilesRendered = []
        if (Array.isArray(tune.tuneFiles) && tune.tuneFiles.length > 0) {
            tune.tuneFiles.forEach(function(file, k) {
                if (!file || !file.id) return
                tuneFilesRendered.push("% abcbook-file-id-" + k + ' ' + ensureText(file.id, ""))
                if (file.name) {
                    tuneFilesRendered.push("% abcbook-file-name-" + k + ' ' + ensureText(file.name, ""))
                }
                if (file.type) {
                    tuneFilesRendered.push("% abcbook-file-type-" + k + ' ' + ensureText(file.type, ""))
                }
                if (file.googleId) {
                    tuneFilesRendered.push("% abcbook-file-google-id-" + k + ' ' + ensureText(file.googleId, ""))
                }
                if (file.source) {
                    tuneFilesRendered.push("% abcbook-file-source-" + k + ' ' + ensureText(file.source, ""))
                }
                if (file.pdfPage > 0) {
                    tuneFilesRendered.push("% abcbook-file-pdf-page-" + k + ' ' + ensureNumber(file.pdfPage, 1))
                }
                if (Array.isArray(file.pdfSegments) && file.pdfSegments.length > 0) {
                    tuneFilesRendered.push("% abcbook-file-pdf-segments-" + k + ' ' + JSON.stringify(file.pdfSegments))
                }
                if (file.data) {
                    tuneFilesRendered.push("% abcbook-file-data-" + k + ' ' + ensureText(file.data, ""))
                }
            })
        }
        if (tune.activeFile) {
            tuneFilesRendered.push("% abcbook-active-file " + ensureText(tune.activeFile, ""))
        }
        var recordingsRendered = []
        if (Array.isArray(tune.recordings)&& tune.recordings.length > 0) {
            tune.recordings.forEach(function(file,k) {
                if (file.googleDocumentId) {
                    recordingsRendered.push("% abcbook-recording-google-document-id-"+k + ' ' +  ensureText(file.googleDocumentId,"") )
                }
                if (file.type) {
                        recordingsRendered.push("% abcbook-recording-type-"+k + ' ' +  ensureText(file.type,"") )
				}
				if (file.name) {
					recordingsRendered.push("% abcbook-recording-name-"+k + ' ' +  ensureText(file.name,"") )
				}
				if (file.data) {  
					recordingsRendered.push("% abcbook-recording-data-"+k + ' ' +  ensureText(file.data,"") )
				}
            })
        }
        //if (voicesAndNotes.length === 0) {
            //voicesAndNotes.push('V:default')
            //voicesAnd Notes.push('|')
        //}
        var otherHeaders = renderOtherHeaders(tune)
        var infoHeaderText = renderAbcInfoHeaderLines(tune).join("\n")
        if (infoHeaderText) infoHeaderText += "\n"
        var sourceBookComments = renderSourceBookCommentLines(tune).join("\n")
        if (sourceBookComments) sourceBookComments += "\n"
        var finalAbc = "\nX: "+tuneNumber + "\n" 
                    + titleHeaderText
                    + composerHeaderText
                    + genreHeaderText
                    + books
                    + ensure(tune.meter,"M:"+ensureText(tune.meter)+ "\n" )
                    + ensure(tune.noteLength, "L:" + ensureText(tune.noteLength) + "\n" )
                    + ensure(tune.rhythm, "R: "+  ensureText(tune.rhythm) + "\n" )
                    + tempoLine
                    + infoHeaderText
                    + otherHeaders
                    + (tune.backgroundInfo && typeof tune.backgroundInfo === 'string' && tune.backgroundInfo.trim()
                      ? tune.backgroundInfo.trim().split('\n').map(function(l) { return 'H:' + l }).join('\n') + '\n'
                      : '')
                    + ensure(tune.key, "K:"+ensureText(tune.key)+ "\n" )
                    + ((voicesAndNotes.length > 0) ? voicesAndNotes.join("\n") + "\n" : '')
                    + renderBlockLyricsAbc(tune)
                    + (tune.timingScaffold ? '% abcbook-timing-scaffold true\n' : '')
                    + "% abcbook-tune_id " + ensureText(tune.id) + "\n" 
                    + "% abcbook-tune_composer_id " + ensureText(tune.composerId) + "\n" 
                    + sourceBookComments
                    + ((linksRendered.length > 0) ? linksRendered.join("\n") + "\n" : '')
                    + renderPlayalongTakesAbc(tune)
                    + "% abcbook-boost " +  ensureNumber(boost,0) + "\n" 
                    + (tune.starred ? "% abcbook-starred true\n" : '')
                    + (tune.autoEnrichOnOpen ? "% abcbook-auto-enrich pending\n" : '')
                    + "% abcbook-difficulty " +  ensureNumber(tune.difficulty,0) + "\n" 
                    + "% abcbook-lyrics-scroll-speed " + ensureNumber(tune.lyricsScrollSpeed > 0 ? tune.lyricsScrollSpeed : 1, 1) + "\n"
                    + (tune.lyricsScrollDurationSec > 0
                      ? "% abcbook-lyrics-scroll-duration " + ensureNumber(tune.lyricsScrollDurationSec, 0) + "\n"
                      : '')
                    // Device-local display settings (zoom, viewMode, notationFit,
                    // activeVoices, tablature*) are not exported — see tuneDisplaySettings.js
                    + "% abcbook-tags " +  ((Array.isArray(tune.tags) && tune.tags.length > 0) ? tune.tags.join(",") : '') + "\n"
                    + (Array.isArray(tune.albums) && tune.albums.length > 0
                      ? tune.albums.map(function(album) {
                        return '% abcbook-albums ' + ensureText(album) + '\n'
                      }).join('')
                      : '')
                    + "% abcbook-capo " +  ensureText(tune.capo) + "\n"
                    + "% abcbook-transpose " +  ensureText(tune.transpose) + "\n" 
                    + "% abcbook-tuning " +  ensureText(tune.tuning) + "\n" 
                    + renderPlaybackAbcbookFields(tune)
                    + "% abcbook-lastupdated " +  ensureNumber(tune.lastUpdated) + "\n" 
                    + "% abcbook-src-url " +  ensureText(tune.srcUrl) + "\n" 
                    + "% abcbook-soundfonts " +  ensureText(tune.soundFonts) + "\n" 
                    + "% abcbook-repeats " +  ensureText(tune.repeats,"1") + "\n" 
                    + ((tune.transpose < 0 || tune.transpose > 0) ? '%%MIDI transpose '+tune.transpose + "\n" : '')
                    + ((renderTimedJsonFields(tune).length > 0) ? renderTimedJsonFields(tune).join("\n") + "\n" : '')
                    // Do not ensureText/trim — that strips the trailing newline and glues
                    // the next % abcbook-file-* line onto the last abc comment.
                    + (Array.isArray(tune.abccomments) && tune.abccomments.length > 0
                      ? tune.abccomments.join("\n") + "\n"
                      : '')
                    + ((filesRendered.length > 0) ? filesRendered.join("\n") + "\n" : '')
                    + ((tuneFilesRendered.length > 0) ? tuneFilesRendered.join("\n") + "\n" : '')
                    + ((recordingsRendered.length > 0) ? recordingsRendered.join("\n") + "\n" : '')

        
        return finalAbc
      } else {
        return ''
      }
    }
    
    function cleanTempo(tempoIn) {
        var tempo = new String(tempoIn)
        if (tempo && tempo.trim)  {
            var parts = tempo.trim().split('=')
            if (parts.length > 0) {
                var t = parseInt(parts[parts.length-1])
                if (t > 0) return t
                else return 100 
            } else return ''
        } else return ''
    }
     function json2abc_print(tune) {
      if (tune) {
        normalizeGenre(tune)
        var titleHeaderText = renderBibliographicTitleLines(tune).join("\n")
        if (titleHeaderText) titleHeaderText += "\n"
        var composerHeaderText = renderBibliographicComposerLines(tune).join("\n")
        if (composerHeaderText) composerHeaderText += "\n"
        var genreHeaderText = renderBibliographicGenreLines(tune).join("\n")
        if (genreHeaderText) genreHeaderText += "\n"
        var voicesAndNotes=[]
        if (tune.voices) {
            Object.keys(tune.voices).forEach(function(voice) {
                if (Array.isArray(tune.voices[voice].notes)) {
                    voicesAndNotes.push("V:"+voice+" "+tune.voices[voice].meta)
                    tune.voices[voice].notes.forEach(function(noteLine) {
                        voicesAndNotes.push(noteLine)
                    })
                }
            })
        }
        var tuneNumber = tune && tune.meta && parseInt(tune.meta.X) !== NaN && tune.meta.X >= 0 ? tune.meta.X : parseInt(Math.random()*100000)
        var finalAbc = "\nX: "+tuneNumber + "\n" 
                    + titleHeaderText
                    + composerHeaderText
                    + genreHeaderText
                    + "M:"+ensureText(tune.meter)+ "\n" 
                    + "L:" + ensureText(tune.noteLength) + "\n" 
                    + "R: "+  ensureText(tune.rhythm) + "\n" 
                    + (cleanTempo(tune.tempo) > 0 ?  "Q: "+ getBeatLength(tune.meter)+'='+ ensureText(cleanTempo(tune.tempo)) + "\n"  : "")
                    + "K:"+ensureText(tune.key)+ "\n" 
                    + ((voicesAndNotes.length > 0) ? voicesAndNotes.join("\n") + "\n" : '')
                    //+ renderWordHeaders(tune)
                    + "% abcbook-capo " +  ensureText(tune.capo) + "\n"
                    + "% abcbook-transpose " +  ensureText(tune.transpose) + "\n"
                    + renderPlaybackAbcbookFields(tune)
                    + "% abcbook-tune_id " + ensureText(tune.id) + "\n" 
        return finalAbc
      } else {
        return ''
      }
    }
    

     function json2abc_cheatsheet(tune) {
      if (tune) {
        var voicesAndNotes=[]
        if (tune.voices) {
            Object.keys(tune.voices).forEach(function(voice) {
                if (Array.isArray(tune.voices[voice].notes)) {
                    //voicesAndNotes.push("V:"+voice+" "+tune.voices[voice].meta)
                    tune.voices[voice].notes.forEach(function(noteLine) {
                        voicesAndNotes.push(noteLine)
                    })
                    
                    //voicesAndNotes.push(firstBars.slice(4).join("|"))
                    
                }
            })
        }
        var firstBars = voicesAndNotes.join("").trim().split("|").slice(0,6)
        var tuneNumber = tune && tune.meta && parseInt(tune.meta.X) !== NaN && tune.meta.X >= 0 ? tune.meta.X : parseInt(Math.random()*100000)
        var finalAbc = "\nX: "+tuneNumber + "\n" 
                    + "T: " + ensureText(tune.name) + "\n" 
                    + "M:"+ensureText(tune.meter)+ "\n" 
                    + "L:" + ensureText(tune.noteLength) + "\n" 
                    + (cleanTempo(tune.tempo) > 0 ?  "Q: "+ getBeatLength(tune.meter)+'='+ ensureText(cleanTempo(tune.tempo)) + "\n"  : "")
                    + "R: "+  ensureText(tune.rhythm) + "\n" 
                    + "K:"+ensureText(tune.key)+ "\n" 
                    + firstBars.join("|")
                    + "% abcbook-capo " +  ensureText(tune.capo) + "\n"
                    + "% abcbook-transpose " +  ensureText(tune.transpose) + "\n"
                    + renderPlaybackAbcbookFields(tune)
                    + "% abcbook-tune_id " + ensureText(tune.id) + "\n" 
                    
        
        return finalAbc
      } else {
        return ''
      }
    }

    //// JUST FIRST THREE BARS AND NO CHORDS
    //function json2shortabc(tune) { 
      //if (tune) {
        //var setting = settingFromTune(tune)
        //var abc = setting.abc
        
        //if (!abc || !abc.trim) {
          //return emptyABC(getTuneName(tune))
        //}
       
          //abc = abc.trim()
          //// remove strings from abc
          //var next = abc.indexOf('"')
          //while (next !== -1) {
            //var nextClose = abc.indexOf('"', next+1)
            //if ((nextClose !== -1) && (nextClose > next)) {
              //abc = abc.slice(0,next) + abc.slice(nextClose+1)
            //} else {
              //abc = abc.slice(0,next) + abc.slice(next + 1)
            //}
            //next = abc.indexOf('"')
          //}
          //// strip start repeats
          //abc = abc.replace("|:","|")
          //// first three bars
          //var shortAbcParts = []
          //shortAbcParts = abc.split("|").slice(0,4)
          //var shortAbc = ''
          //// handle placement of text(song title)
          //if (shortAbcParts.length > 3) {
            //// tune starts at first bar
            //if (shortAbcParts[0].trim() === '') {
              //var ts = getTuneMeter(tune)
              //if (ts !== '') {
                //shortAbc = shortAbc + "[M:"+ts+ "] "
              //} 
              //shortAbc = shortAbc + "[K:"+setting.key+ "] "
              //// tune title 
              //shortAbc = shortAbc  + '"' + songNumberForDisplay(songNumber) + '. '+getTuneName(tune)+ '"' +  shortAbcParts.slice(1).join("|") 
              //// lead in note
            //} else {
              //var ts = getTuneMeter(tune)
              //if (ts !== '') {
                //shortAbc = shortAbc + "[M:"+ts+ "] "
              //} 
              //shortAbc = shortAbc + "[K:"+setting.key+ "] "
              //shortAbc = shortAbc  + '"' + songNumberForDisplay(songNumber) + '. ' +getTuneName(tune)+ '"' +  shortAbcParts.join("|") 
            //}
          //}
          //shortAbc = "\nX: "+songNumber + "\n" + shortAbc + "\n"
          //return shortAbc
      //} else {
        //return ''
      //}
       
    //}


    function sliceIntoChunks(arr, chunkSize) {
        const res = [];
        for (let i = 0; i < arr.length; i += chunkSize) {
            const chunk = arr.slice(i, i + chunkSize);
            res.push(chunk);
        }
        return res;
    }



    function settingFromTune(tune) {
        var useSetting = parseInt(tune.useSetting) > 0 ? parseInt(tune.useSetting) : 0
        if (tune && tune.settings && tune.settings.length > useSetting) {
          return tune.settings[useSetting]
        } else {
            return {key:'', abc: ''}
        }
    }


    function getTextFromSongline(songline) {
        if (!songline) return
        var last = songline.lastIndexOf(']')
        if (last !== -1) {
            return songline.slice(last + 1)
        } else {
            return songline
        }
    }

    function getRhythmTypes() {
        return {
        'jig': '6/8',
        'reel':  '4/4',
        'slip jig':  '9/8',
        'hornpipe':  '4/4',
        'polka':  '2/4',
        'slide':  '12/8',
        'waltz':  '3/4',
        'barndance':  '4/4',
        'strathspey':  '4/4',
        'three-two':  '3/2',
        'mazurka':  '3/4'
      }
    }
    
    function getTimeSignatureTypes() {
        return ['2/4','3/4','4/4','6/8','9/8','12/8','3/2']
    }

    function timeSignatureFromTuneType(type) {
      var types = getRhythmTypes()
      if (types.hasOwnProperty(type)) {
        return types[type]
      } else {
        return ''
      }
    }

    function emptyABC(name, number = 0) {
      return "\nX:"+number+"\n" + 'T:' +name +  '' + "\n"
    }

    function songNumberForDisplay(tune) {
      if (tune && parseInt(tune.songNumber) > 0) {
        return  parseInt(tune.songNumber) + 1
      }
      return 1
    }

    function stripText(text) {
      var result = ''
      if (text && text.trim) {
          result = text.trim().replace(/[^a-zA-Z0-9 ]/g, ' ').toLowerCase().trim()
      }
      return result
    }




    /**
     * TO JSON
     */

    function abc2Tunebook(abc, options) {
      // Deleted-tune tombstones and performance-set sections are appended to the
      // document as comment lines and are parsed separately. Strip them here so
      // they do not get turned into phantom tunes.
      var withoutSets = stripPracticeListLines(stripPlaylistLines(stripPerformanceSetLines(abc || '')))
      var cleaned = withoutSets.split('\n').filter(function(line) {
        return !line.trim().startsWith('% abcbook-deleted-tune')
      }).join('\n')
      var parts = cleaned.split('X:')
      var final = []
      var tuneBook = parts.forEach(function(v,k) {
        if (v && v.trim().length > 0) {
          final.push(abc2json('X:'+v, options))
        } 
      })
      return final
    }

    //function isAliasLine(line) {
        //return (line.startsWith("N: AKA: "))
    //}

    //function isMetaLine(line) {
        //if (line[1] === ":" && line[0] !== "|" && line[2] !== "|" ) {
            //// avoid alias lines
            //return isAliasLine(line) ? false : true
            
        //} else {
            //return false
        //}
    //}


    //function getMetaFromAbc(abc) {
      //var parts = abc.split("\n")
      //var meta = {}
      //function stashMeta(key,val) {
          //if (meta.hasOwnProperty(key)) {
              //meta[key] = meta[key] + "\n" + val
          //} else {
              //meta[key] = val
          //}
      //}
      
      //parts.map(function(part) {
        //if (isMetaLine(part) && !isAliasLine(part)) {
            //if (part[0] === 'T') {
                //stashMeta(part[0],stripLeadingNumber(part.slice(2).trim()))
            //} else {
                //stashMeta(part[0],part.slice(2).trim())
            //}
           
        //}
      //})
      //return meta
      
    //}


    function getCommentsFromAbc(abc) {
        if (!abc) return
        var parts = abc.split('\n')
        return parts.map(function(line) { 
            // exclude custom comments
            if (line.startsWith('% ') && !line.startsWith('% abc-')) {
                return line + "\n"
            } else {
                return ''
            }    
        }).join("")
    }

    function getNotesFromAbc(abc, parseNotes = false, barsPerLine = 0) {
        var useBarsPerLine = (barsPerLine > 0) ? parseInt(barsPerLine) : 4
        
        if (!abc || !abc.split) return ''
        var parts = abc.split("\n")
        var noteLines = []
        for (var lineNumber in parts) {
            var line = parts[lineNumber]
            if (isNoteLine(line) && (line.trim().length > 0)) {
                noteLines.push(line+"\n")
            }    
            
        }
        var notes = noteLines
        if (parseNotes) {
           //  try parse and rejoin notes to standardise (but lose force line breaks)
            try {
                var tunes = abcjs.extractMeasures(abc)
                if (tunes.length > 0) {
                    notes = tunes[0].measures.map(function(measure,measureNumber) {
                        var nl = ''
                        var offset = 0
                        if (tunes[0].hasPickup) offset = 1
                        if (((measureNumber - offset) % useBarsPerLine) === (useBarsPerLine - 1) ) nl="\n" 
                        return measure.abc + nl
                    })
                }
            } catch(e) {
                console.log(e)
            }
        }

        return notes.join("")
    }


    //function getAliasesFromAbc(abc) {
        //if (!abc) return
        //var aliases=[]
        //var first = abc.indexOf('N: AKA: ')
        //while (first !== -1) {
            //var parts = abc.slice(first + 7).split("\n")
            //var aliasParts = parts[0].split(",")
            //aliasParts.forEach(function(aliasPart) {
              //aliases.push(aliasPart.trim())
            //})
            //first = abc.indexOf('N: AKA:', first + 1)
        //} 
        //return aliases
    //}


    function getMetaValueFromAbc(key,abc) {
        var found = ''
        if (abc && abc.split && key && key.trim && key.trim().length > 0)  {
            var parts = abc.split("\n")
            for (var partKey in parts) {
                var part = parts[partKey].trim()
                if (part.startsWith(key + ':')) {
                    found = part.trim().slice(2)
                    break;
                }
            }
        }
        return found
    }

    function getKeyedCommentFromAbc(key,abc) {
        if (!abc) return ''
        var first = abc.indexOf('% abc-'+key)
        if (first === 0) {
            var parts = abc.slice(first + 6 + key.length).split("\n")
            return ensureText(parts[0].trim())
        } else {
            return ''
        }
    }
    
    function getSettingCommentsFromAbc(abc) {
        if (!abc) return
        var parts = abc.split('\n')
        return parts.map(function(line) { 
            // exclude custom comments
            if (line.startsWith('% ') && !line.startsWith('% abc-')) {
                return line + "\n"
            } else {
                return ''
            }    
        }).join("")
    }

    function removeAbcInnerStrings(abc) {
      if (abc ) {
            // remove strings from abc notes
          abc = new String(abc).trim()
          var next = abc.indexOf('"')
          while (next !== -1) {
            var nextClose = abc.indexOf('"', next+1)
            if ((nextClose !== -1) && (nextClose > next)) {
              // strip string
              abc = abc.slice(0,next) + abc.slice(nextClose+1)
            } else {
              abc = abc.slice(0,next) + abc.slice(next + 1)
            }
            next = abc.indexOf('"')
          }
      }
      return abc ? abc : ''
    }
    
    function hasChords(abcNotes) {
        return (abcNotes && abcNotes.indexOf('"') !== -1)
    }
      //var chords = getInnerStrings(abc)
      //var haveChords = false;
      //// any match success
      //chords.map(function(chord,k) {
          //if (chord.indexOf('"') !== -1) {
              //haveChords = true;
          //}
         ////if (isChord(chord)) {
            ////haveChords = true;
         ////}
      //})
      //return haveChords
    //}
    
    function getInnerStrings(abc) {
        var s = []
      if (abc) {
            // remove strings from abc
          abc = abc.trim()
          var next = abc.indexOf('"')
          while (next !== -1) {
            var nextClose = abc.indexOf('"', next+1)
            if ((nextClose !== -1) && (nextClose > next)) {
              // strip string
              s.push(abc.slice(next+1,nextClose))
              abc = abc.slice(0,next) + abc.slice(nextClose+1)
            } else {
              abc = abc.slice(0,next) + abc.slice(next + 1)
            }
            next = abc.indexOf('"')
          }
      }
      return s
    }



    function safeString(text) {
        if (text) {
            text = text.replaceAll(" ",'_')
            text = text.replaceAll(",",'_')
            text = text.replaceAll(".",'_')
            text = text.replaceAll("[",'_')
            text = text.replaceAll("]",'_')
            text = text.replaceAll("(",'_')
            text = text.replaceAll(")",'_')
            text = text.replaceAll("?",'_')
        }
        return  text
    }


    /**
     * Manipulate timing of abc string
     * eg multiplier=2 then  A2 b/ b/2 => A b b 
     */
      
    function isOctaveModifier(letter) {
        return (letter === ',' || letter === "'") 
    }
  
    function fixNotes(abc, barsPerLine= 0) {
        return getNotesFromAbc(abc,true, barsPerLine)
    }

    function fixNotesBang(abc) {
        return convertSessionLineBreaks(getNotesFromAbc(abc))
    }
    
    function multiplyAbcTiming(multiplier,abc) {
        var measuresRaw = abcjs.extractMeasures(abc)
        var measures = measuresRaw[0].measures
        var newMeasures = measures.map(function(m) {
          var abc = m.abc
          var c = 0
          var newAbc = []
          var inQuote = false
          var inCurlyBracket = false
          var inSquareBracket = false
          while (c < abc.length) {
            var modifier = ''
            var symbol = abc[c]
            if (symbol === '"') {
                inQuote =!inQuote
            }
            if (symbol === '{') {
                inCurlyBracket = true
            }
            if (symbol === '}') {
                inCurlyBracket = false
            }
            if (symbol === '[') {
                inSquareBracket = true
            }
            if (symbol === ']') {
                inSquareBracket = false
            }
            var newSymbol = null
            var nextSymbol = abc[c+1]
            var nextNextSymbol = null
            var nextNextNextSymbol = null
            if (!inQuote && !inCurlyBracket && !inSquareBracket && isNoteLetter(symbol)) {
                if (c < abc.length + 1) {
                    nextSymbol = abc[c+1]
                    if (isOctaveModifier(nextSymbol)) {
                        var modifier = abc[c+1]
                        nextSymbol = nextSymbol = abc[c+2]
                        if (c < abc.length + 3) {
                            nextNextSymbol = abc[c+3]
                            
                            if (c < abc.length + 4) {
                                nextNextNextSymbol = abc[c+4]
                            }
                        }
                        var result = symbolsToFraction(nextSymbol, nextNextSymbol,nextNextNextSymbol) 
                        var asFraction = new Fraction(result.number.top,result.number.bottom)
                        var m = decimalToFraction(multiplier)
                        var multiplierFraction = new Fraction(m.top,m.bottom)
                        var finalFraction = asFraction.multiply(multiplierFraction)
                        newAbc.push(symbol + modifier + abcFraction(finalFraction.numerator, finalFraction.denominator))
                        c = c + result.symbolsUsed  + 2
                    } else {
                        if (c < abc.length + 2) {
                            nextNextSymbol = abc[c+2]
                            if (c < abc.length + 3) {
                                nextNextNextSymbol = abc[c+3]
                            }
                        }
                        var result = symbolsToFraction(nextSymbol, nextNextSymbol,nextNextNextSymbol) 
                        var asFraction = new Fraction(parseInt(result.number.top),parseInt(result.number.bottom))
                        var m = decimalToFraction(multiplier)
                        var multiplierFraction = new Fraction(parseInt(m.top),parseInt(m.bottom))
                        var finalFraction = asFraction.multiply(multiplierFraction)
                        newAbc.push(symbol + modifier + abcFraction(finalFraction.numerator, finalFraction.denominator))
                        c = c + result.symbolsUsed  + 1
                    }
                } else {
                    newAbc.push(symbol)
                    c++
                }
            } else {
                newAbc.push(symbol)
                c++
            }
          }
          m.abc = newAbc.join('')
          return m 
        })
        return generateAbcFromMeasures(newMeasures)
    }

    function generateAbcFromMeasures(measures) {
        return measures.map(function(measure) {
            var abc = measure.abc
            return abc 
        }).join(" ")
    }

    // eg /4 becomes 0.25, /2 becomes 0.5, 4 becomes 4.0
    function symbolsToFraction(a,b,c) {
        if (isSlash(a)) {
            if (isDigit(b)) {
                if (isDigit(c)) {
                    var combined = parseInt(b + '' + c)
                    return {symbolsUsed: 3, number: {top: 1, bottom: combined}}
                } else {
                    return {symbolsUsed: 2, number: {top: 1, bottom: b}}
                }
            } else {
                return {symbolsUsed: 1, number: {top: 1, bottom: 2}}
            }
        } else {
            if (isDigit(a)) {
                if (isDigit(b)) {
                    var combined = parseInt(a + '' + b)
                    return {symbolsUsed: 2, number: {top: combined, bottom: 1}}
                } else {
                    return {symbolsUsed: 1, number: {top: a, bottom: 1}}
                }
            } else {
                return {symbolsUsed: 0, number: {top: 1, bottom: 1}}
            }
        }
    }

    function gcd(a, b) {
        return (b) ? gcd(b, a % b) : a;
    }

    var decimalToFraction = function (_decimal) {
        if (_decimal == parseInt(_decimal)) {
            var a = {
                top: parseInt(_decimal),
                bottom: 1,
                display: parseInt(_decimal) + '/' + 1
            };
            return a
        }
        else {
            var top = _decimal.toString().includes(".") ? _decimal.toString().replace(/\d+[.]/, '') : 0;
            var bottom = Math.pow(10, top.toString().replace('-','').length);
            if (_decimal >= 1) {
                top = +top + (Math.floor(_decimal) * bottom);
            }
            else if (_decimal <= -1) {
                top = +top + (Math.ceil(_decimal) * bottom);
            }

            var x = Math.abs(gcd(top, bottom));
            var a = {
                top: (top / x),
                bottom: (bottom / x),
                display: (top / x) + '/' + (bottom / x)
            };
            return a
        }
    }

    function abcFraction(top, bottom) {
        if (bottom === 1) {
            if (top === 1) {
                return ''
            } else {
                return top
            }
        } else {
            if (bottom === 2) {
                return "/"
            } else {
                return "/" + bottom
            }
        }
    }


    function isNoteLetter(a) {
        if ("zabcdefgABCDEFG".indexOf(a) !== -1) {
            return true
        } else {
            return false
        }
    }
    function isDigit(a) {
        if ("123456789".indexOf(a) !== -1) {
            return true
        } else {
            return false
        }
    }
    function isSlash(a) {
        if (a === "/") {
            return true
        } else {
            return false
        }
    }


    //function getTuneId(tune) {
        //if (tune && tune.meta && tune.meta.hasOwnProperty("X")) {
            //return ensureText(tune.meta["X"],tune.id)
        //} else return tune.id
    //}

    //function getTuneName(tune) {
        //if (tune && tune.meta && tune.meta.hasOwnProperty("T")) {
            //return ensureText(tune.meta["T"],tune.name)
        //} else return tune.name
    //}
    
    //function getTuneBookName(tune) {
        //if (tune && tune.meta && tune.meta.hasOwnProperty("B")) {
            //return ensureText(tune.meta["B"],tune.book)
        //} else return tune.book
    //}
    
    //function getTuneType(tune) {
        //if (tune && tune.meta && tune.meta.hasOwnProperty("R")) {
            //return ensureText(tune.meta["R"],tune.type)
        //} else return tune.type
    //}

    //function getTuneNoteLength(tune) {
        //if (tune && tune.meta && tune.meta.hasOwnProperty("L")) {
            //return ensureText(tune.meta["L"],'1/8')
        //} else return '1/8'
    //}
    function getTuneMeter(tune) {
        return ensureText(tune.meter,timeSignatureFromTuneType(tune.type))
    }

    function cleanMetaData(meta) {
        var final = {}
        if (meta) Object.keys(meta).forEach(function(key) {
          if (requiredHeaders.indexOf(key) !== -1) {
              final[key] = meta[key]
          }
        })
        return final
    }

     function ensureText(val, defaultVal) {
        if (val !== null && val !== undefined && (val && String(val).trim().length > 0)) {
          return String(val).trim()
        } else {
          return defaultVal && defaultVal.trim ? defaultVal.trim() : ''
        }
    }

    function ensureNumber(val) {
        if (val && val > 0) {
          return val
        } else {
          return 0
        }
    }
    function stripLeadingNumber(text) {
      if (text && text.split) {
        var tParts = text ? text.split(".") : []
        var name = ensureText(tParts.length > 1 ? tParts[1].trim() : text.trim())
        return name
      } else {
        return text
      }
    }


    function getBeatDuration(meter) {
        var parts = meter ? meter.split("/") : null
        var duration = parts && parts.length > 1 && parts[1] > 0 ? parseInt(parts[1]) : 0
        return duration
    }
    
    function getBeatsPerBar(meter) {
        var model = getBarModel(meter, null)
        return model.beatCount
    }

    function getTempo(tune) {
        var tempo = 100
        var tempoString = tune && tune.tempo ? tune.tempo : '1/4=100'
        var tempoStringParts = removeAbcInnerStrings(tempoString).split("=")
        if (tempoStringParts.length > 1) {
            tempo = parseInt(tempoStringParts[1])
            if (!tempo > 0) tempo = 100
        } else {
            tempo = parseInt(tempoStringParts[0])
            if (!tempo > 0) tempo = 100
        }
        return tempo
    }

    //function getM
    //function getMilliSecondsPerMeasure(tune, forceTempo) {
       //var meter = ensureText(getTuneMeter(tune), '4/4')
       //var beats = getBeatsPerBar(meter)
       //var tempo = forceTempo ? forceTempo : getTempo(tune)
       //return 60000/tempo * beats
    //}

    //function getReviewTempo(tune) {
      //if (tune) {
        //var boost = tune.boost > 0 ? tune.boost : 0
        //var useBoost = boost > 20 ? 20 : boost
        ////Math.min(boost,50)
        //var tempo = 30 + useBoost * 5
        ////tempo+= 300 // testing
          
        //return tempo;
      //} else {
        //return 100
      //}
    //}
    
        
    function isChord(chord) {
      var chordMatches = [
          'A','B','C','D','E','F','G',  
          'Am','Bm','Cm','Dm','Em','Fm','Gm',
          'Amin','Bmin','Cmin','Dmin','Emin','Fmin','Gmin',
          'Amaj','Bmaj','Cmaj','Dmaj','Emaj','Fmaj','Gmaj',
          'Adim','Bdim','Cdim','Ddim','Edim','Fdim','Gdim',
          'Aaug','Baug','Caug','Daug','Eaug','Faug','Gaug',
          'Asus','Bsus','Csus','Dsus','Esus','Fsus','Gsus',
          'A7','B7','C7','D7','E7','F7','G7',
          'A9','B9','C9','D9','E9','F9','G9',
          
          'Ab','Bb','Cb','Db','Eb','Fb','Gb',  
          'Abm','Bbm','Cbm','Dbm','Ebm','Fbm','Gbm',
          'Abmin','Bbmin','Cbmin','Dbmin','Ebmin','Fbmin','Gbmin',
          'Abmaj','Bbmaj','Cbmaj','Dbmaj','Ebmaj','Fbmaj','Gbmaj',
          'Abdim','Bbdim','Cbdim','Dbdim','Ebdim','Fbdim','Gbdim',
          'Abaug','Bbaug','Cbaug','Dbaug','Ebaug','Fbaug','Gbaug',
          'Absus','Bbsus','Cbsus','Dbsus','Ebsus','Fbsus','Gbsus',
          'Ab7','Bb7','Cb7','Db7','Eb7','Fb7','Gb7',
          'Ab9','Bb9','Cb9','Db9','Eb9','Fb9','Gb9',
          
          'A#','B#','C#','D#','E#','F#','G#',  
          'A#m','B#m','C#m','D#m','E#m','F#m','G#m',
          'A#min','B#min','C#min','D#min','E#min','F#min','G#min',
          'A#maj','B#maj','C#maj','D#maj','E#maj','F#maj','G#maj',
          'A#dim','B#dim','C#dim','D#dim','E#dim','F#dim','G#dim',
          'A#aug','B#aug','C#aug','D#aug','E#aug','F#aug','G#aug',
          'A#sus','B#sus','C#sus','D#sus','E#sus','F#sus','G#sus',
          'A#7','B#7','C#7','D#7','E#7','F#7','G#7',
          'A#9','B#9','C#9','D#9','E#9','F#9','G#9'
      ]
      return chordMatches.indexOf(chord.trim()) !== -1
    }
    
    
    const tablatureConfig = buildLegacyTablatureConfigMap()
  
  function getTuneHash(tune) {
    if (tune) {
        var voicesAndNotes=[]
        if (tune.voices) {
            Object.keys(tune.voices).forEach(function(voice) {
                if (Array.isArray(tune.voices[voice].notes)) {
                    //voicesAndNotes.push("V:"+voice+" "+tune.voices[voice].meta)
                    tune.voices[voice].notes.forEach(function(noteLine) {
                        voicesAndNotes.push(noteLine)
                    })
                }
            })
        }
        const hashString = tune.title + tune.tempo+tune.meter+tune.transpose+tune.key+tune.soundFonts + (voicesAndNotes.join("\n") )
        var hash = utils.hash(hashString)
        return hash
    } else {
        return utils.hash(new String(Math.random()*100000000000000000).toString())
    }
  }
  
  function getNotes(tune) {
      var voicesAndNotes=[]
      if (tune) {
        
        if (tune.voices) {
            Object.keys(tune.voices).forEach(function(voice) {
                if (Array.isArray(tune.voices[voice].notes)) {
                    //voicesAndNotes.push("V:"+voice+" "+tune.voices[voice].meta)
                    tune.voices[voice].notes.forEach(function(noteLine) {
                        voicesAndNotes.push(noteLine)
                    })
                }
            })
        }
        return voicesAndNotes.join("\n")
    }
    return voicesAndNotes.join("\n")
  }
  
  function getTuneImportHash(tune) {
    if (tune) {
        var voicesAndNotes=[]
        if (tune.voices) {
            Object.keys(tune.voices).forEach(function(voice) {
                if (Array.isArray(tune.voices[voice].notes)) {
                    //voicesAndNotes.push("V:"+voice+" "+tune.voices[voice].meta)
                    tune.voices[voice].notes.forEach(function(noteLine) {
                        voicesAndNotes.push(noteLine)
                    })
                }
            })
        }

        // Include block lyrics (W:/words) so ChordPro-preserved placement
        // participates in content-hash dedupe; wLines alone miss that store.
        // Delimit fields so titles cannot collide via adjacency with tempo/meter/etc.
        const titlePart = String(tune.title || tune.name || '')
        const wLinesPart = Array.isArray(tune.wLines) ? tune.wLines.join("\n") : ''
        const wordsPart = Array.isArray(tune.words) ? tune.words.join("\n") : ''
        const hashString = [
          titlePart,
          String(tune.tempo != null ? tune.tempo : ''),
          String(tune.meter != null ? tune.meter : ''),
          String(tune.transpose != null ? tune.transpose : ''),
          String(tune.key != null ? tune.key : ''),
          String(tune.soundFonts != null ? tune.soundFonts : ''),
          voicesAndNotes.join("\n"),
          wLinesPart,
          wordsPart,
        ].join('\u0001')
        var hash = utils.hash(hashString)
        return hash
    } else {
        return utils.hash(new String(Math.random()*100000000000000000).toString())
    }
  }
  
  //function getTuneAllHash(tune) {
    //var hash = utils.hash(JSON.stringify(tune))
    //return hash
  //}

  function tunesToAbc(tunes, deletedTunes) {
    var res = Object.values(tunes).map(function(tune, k) {
      if (!tune) return json2abc(tune)
      // Never mutate stored meta: some libraries have meta as the string
      // "[object Object]" (or other non-plain values), which throws on .X =.
      var meta = normalizeTuneMeta(tune.meta)
      meta.X = k
      return json2abc(Object.assign({}, tune, { meta: meta }))
    }).join("\n")
    var tombstones = renderDeletedTunesToAbc(deletedTunes)
    if (tombstones) {
      res = res ? res + '\n' + tombstones : tombstones
    }
    return res

  }
  
  
  
    function getNoteLengthFraction(noteLength) {
        var parts = parseNoteLengthParts(noteLength, '4/4')
        return new Fraction(parts.num, parts.den)
    }
    
    function getNoteLengthsPerBar(noteLength, meter) {
        var model = getBarModel(meter, noteLength)
        return model.unitSlotsPerBar
    }
      
    function tunesToLinkList (tunes) {
        return tunes.map(function(tune) {
            return tune.name  + (tune.composer ? '  -  ' + tune.composer : '') + "\n" + (Array.isArray(tune.links) ? tune.links.map(function(link) {return link.link}).join("\n") : "\n")
        }).join("\n\n")
    }

    return {abc2json, json2abc, json2abc_print, json2abc_cheatsheet, abc2Tunebook, ensureText, ensureNumber, isNoteLine, isCommentLine, isMetaLine, isDataLine,isVoiceMeta, justNotes,justNotesNoMeta,  getRhythmTypes, timeSignatureFromTuneType, fixNotes, fixNotesBang, multiplyAbcTiming, getTempo, hasChords, getBeatsPerBar, getBeatDuration, cleanTempo, getBeatLength, tablatureConfig, getNotesFromAbc, getTuneHash, tunesToAbc, isNoteLetter, isOctaveModifier, symbolsToFraction, decimalToFraction, abcFraction, isChord, getNoteLengthsPerBar, getNoteLengthFraction, getTuneImportHash, getTimeSignatureTypes, settingFromTune, emptyABC, getMetaValueFromAbc, getNotes, tunesToLinkList, getBarModel, normalizeMeter}
}
export default useAbcTools;
